import { motion, useReducedMotion } from 'motion/react'
import { Link, useLocation } from 'react-router'
import { defaultSpatial } from '../motion/tokens'
import { Symbol, type SymbolName } from '../ui'
import {
  BEREICH_GRUPPEN,
  bereichFuerPfad,
  type BereichDefinition,
  type BereichGruppe,
} from './bereiche'

export interface NavigationsZiel {
  id?: string
  pfad: string
  label: string
  symbol: SymbolName
  gruppe?: BereichGruppe
}

export const MEHR_ZIEL: NavigationsZiel = {
  id: 'mehr',
  pfad: '/mehr',
  label: 'Mehr',
  symbol: 'more',
}

/** Direkte Ziele gewinnen; Mehr vertritt nur bekannte, ausgeblendete Bereiche. */
export function aktivesZiel(pfad: string, ziele: readonly { pfad: string }[]): string | null {
  const passt = (ziel: string): boolean =>
    ziel === '/' ? pfad === '/' : pfad === ziel || pfad.startsWith(`${ziel}/`)
  const eigenes = ziele.find((ziel) => passt(ziel.pfad))
  if (eigenes) return eigenes.pfad
  return bereichFuerPfad(pfad) && ziele.some((ziel) => ziel.pfad === '/mehr') ? '/mehr' : null
}

function Ziel({
  ziel,
  aktiv,
  variante,
}: {
  ziel: NavigationsZiel
  aktiv: boolean
  variante: string
}) {
  const ruhig = useReducedMotion()
  return (
    <Link
      to={ziel.pfad}
      className={`leiste__ziel${aktiv ? ' leiste__ziel--an' : ''}`}
      aria-current={aktiv ? 'page' : undefined}
    >
      <span className="leiste__zeichen">
        {aktiv ? (
          <motion.span
            className="nav__pille"
            layoutId={`nav-pille-${variante}`}
            transition={ruhig ? { duration: 0 } : defaultSpatial}
          />
        ) : null}
        <Symbol name={ziel.symbol} />
      </span>
      <span className="leiste__wort">{ziel.label}</span>
    </Link>
  )
}

function EinfacheNavigation({
  ziele,
  klasse,
  variante,
}: {
  ziele: readonly NavigationsZiel[]
  klasse: string
  variante: string
}) {
  const ort = useLocation()
  const aktiv = aktivesZiel(ort.pathname, ziele)
  return (
    <nav className={`leiste ${klasse}`} aria-label="Bereiche">
      {ziele.map((ziel) => (
        <Ziel key={ziel.pfad} ziel={ziel} aktiv={ziel.pfad === aktiv} variante={variante} />
      ))}
    </nav>
  )
}

function BreiteNavigation({ bereiche }: { bereiche: readonly BereichDefinition[] }) {
  const ort = useLocation()
  const aktiv = bereichFuerPfad(ort.pathname, bereiche)?.pfad ?? null
  return (
    <nav className="leiste leiste--breit" aria-label="Bereiche">
      {BEREICH_GRUPPEN.map((gruppe) => {
        const ziele = bereiche.filter((bereich) => bereich.gruppe === gruppe.id)
        if (ziele.length === 0) return null
        return (
          <section
            className="leiste__gruppe"
            aria-labelledby={`nav-gruppe-${gruppe.id}`}
            key={gruppe.id}
          >
            <h2 id={`nav-gruppe-${gruppe.id}`}>{gruppe.label}</h2>
            {ziele.map((ziel) => (
              <Ziel key={ziel.pfad} ziel={ziel} aktiv={ziel.pfad === aktiv} variante="breit" />
            ))}
          </section>
        )
      })}
    </nav>
  )
}

export function Navigation({
  kompakt,
  mittel,
  erweitert,
}: {
  kompakt: readonly NavigationsZiel[]
  mittel: readonly NavigationsZiel[]
  erweitert: readonly BereichDefinition[]
}) {
  return (
    <>
      <EinfacheNavigation ziele={kompakt} klasse="leiste--kompakt" variante="kompakt" />
      <EinfacheNavigation ziele={mittel} klasse="leiste--mittel" variante="mittel" />
      <BreiteNavigation bereiche={erweitert} />
    </>
  )
}
